import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, Component, Input } from '@angular/core';
import { IProductDataV3 } from '@df/catalog/product/product';
import { app } from '@df/core/app';
import { ImageModule } from '@df/module-image/image.module';
import { CarouselModule } from '@df/ui-carousel/carousel.module';
import { GridModule } from '@df/ui/atomic/atom/grid/grid.module';
import { ColorOptions, UndefinedOption } from '@df/ui/atomic/atomic-options';
import { findMediaByLabel } from '@df/ui/common/find-media-by-label.pipe';
import { IfSizeDirective } from '@df/ui/common/if-size.directive';
import { UiComponent } from '@df/ui/ui.component';
import { ICMS_CLIENT_CONFIG, NICmsClientConfig } from '@icms/client/config/icms-client.config';
import { ICmsComponent, ICmsContent, NICmsContentComponent, NICmsContentField } from '@icms/core/content';
import { ReadMoreComponent } from '../../../ui/common/read-more.component';
import { Product } from '../product.decorator';

export interface IProductRangeBanner extends NICmsContentComponent.IDataWithStyles {
    items: IProductRangeBannerItem[];
    titleColor?: string;
    descColor?: string;
}

export interface IProductRangeBannerItem {
    image?: string;
    title?: string;
    desc?: string;
}

@Component({
    selector: 'product-range-banner',
    templateUrl: './product-range-banner.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [IfSizeDirective, CommonModule, ReadMoreComponent, ImageModule, CarouselModule, GridModule]
})
@ICmsComponent<ProductRangeBannerComponent>({
    id: 'product-range-banner',
    block: {
        url: 'product-range-banner'
    },
    name: 'Product Range Banner',
    description: 'Product Range Banner Block',
    fields: [
        {
            type: NICmsContentField.EFieldType.label,
            key: 'label-specification',
            title: 'Range Banner'
        },
        <NICmsContentField.IRepeaterField>{
            type: NICmsContentField.EFieldType.repeater,
            key: 'items',
            title: 'Items',
            description: 'Items are overwritten with product data if configured. Save changes to show it outside the editor.',
            itemName: 'Item',
            itemNameField: 'title',
            maxSize: 3,
            fields: [
                {
                    type: NICmsContentField.EFieldType.image,
                    key: 'image',
                    title: 'Image',
                    required: true
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'title',
                    title: 'Title'
                },
                {
                    type: NICmsContentField.EFieldType.text,
                    key: 'desc',
                    title: 'Description'
                }
            ]
        },
        <NICmsContentField.IChoiceField>{
            key: 'titleColor',
            title: 'Title Color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            options: ColorOptions
        },
        <NICmsContentField.IChoiceField>{
            key: 'descColor',
            title: 'Description Color',
            type: NICmsContentField.EFieldType.choice,
            colorPreview: true,
            options: ColorOptions
        },
        <NICmsContentField.IChoiceField>{
            key: 'styles.bg',
            type: NICmsContentField.EFieldType.choice,
            title: 'Background Color',
            options: () => [...UndefinedOption, ...app.injector.get<NICmsClientConfig.IConfig>(ICMS_CLIENT_CONFIG).colors],
            colorPreview: true
        }
    ]
})
export class ProductRangeBannerComponent extends UiComponent implements NICmsContentComponent.IComponent {
    @ICmsContent({
        type: 'blockContent'
    })
    content?: IProductRangeBanner;

    @Input()
    product?: Product<IProductDataV3>;

    @Input()
    variant?: string;

    get hasProductRangeImages(): boolean {
        return !!(this.firstProductRangeImage || this.secondProductRangeImage || this.thirdProductRangeImage);
    }

    get firstProductRangeImage(): string | undefined {
        return this.product?.familyMedia && findMediaByLabel(this.product?.familyMedia, 'image_for_product_range_block_', false);
    }

    get secondProductRangeImage(): string | undefined {
        return this.product?.familyMedia && findMediaByLabel(this.product?.familyMedia, 'image_for_product_range_block_2', false);
    }

    get thirdProductRangeImage(): string | undefined {
        return this.product?.familyMedia && findMediaByLabel(this.product?.familyMedia, 'image_for_product_range_block_3', false);
    }

    get items(): IProductRangeBannerItem[] {
        if (this.hasProductRangeImages) {
            const productRangeItems: IProductRangeBannerItem[] = [];

            if (this.firstProductRangeImage) {
                productRangeItems.push({
                    image: this.firstProductRangeImage,
                    title: this.product?.data?.title_product_range_block,
                    desc: this.product?.data?.text_field_product_range_block
                });
            }

            if (this.secondProductRangeImage) {
                productRangeItems.push({
                    image: this.secondProductRangeImage,
                    title: this.product?.data?.title_product_range_block_2,
                    desc: this.product?.data?.text_field_product_range_block_2
                });
            }

            if (this.thirdProductRangeImage) {
                productRangeItems.push({
                    image: this.thirdProductRangeImage,
                    title: this.product?.data?.title_product_range_block_3,
                    desc: this.product?.data?.text_field_product_range_block_3
                });
            }

            return productRangeItems;
        } else {
            return this.content?.items || [];
        }
    }
}
